import { type CreateGlobalNetworkResponse, type GetGlobalNetworkResponse } from "@woven/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useControlApiClient } from "../../lib/hooks";

export type GlobalNetworkState =
  | { status: "loading" }
  | { status: "ready"; data: GetGlobalNetworkResponse }
  | { status: "error"; message: string };

/** Loads the global network record (or `null` if it hasn't been created yet), with retry. */
export function useGlobalNetwork() {
  const client = useControlApiClient();
  const [revision, setRevision] = useState(0);
  const scope = useMemo(() => ({ client, revision }), [client, revision]);
  const [snapshot, setSnapshot] = useState<{
    scope: typeof scope;
    state: GlobalNetworkState;
  } | null>(null);
  const reload = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    scope.client.getGlobalNetwork(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setSnapshot({ scope, state: { status: "ready", data } });
      },
      (error) => {
        if (!controller.signal.aborted) {
          setSnapshot({ scope, state: { status: "error", message: errorMessage(error) } });
        }
      }
    );
    return () => controller.abort();
  }, [scope]);

  const state: GlobalNetworkState =
    snapshot?.scope === scope ? snapshot.state : { status: "loading" };
  return { state, reload };
}

export interface CreateGlobalNetworkResult {
  submit: () => Promise<CreateGlobalNetworkResponse | null>;
  submitting: boolean;
  error: string | null;
}

/** Creates the global network singleton through the control API placeholder. */
export function useCreateGlobalNetwork(): CreateGlobalNetworkResult {
  const client = useControlApiClient();
  const inFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async () => {
    if (inFlight.current) return null;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      return await client.createGlobalNetwork();
    } catch (cause) {
      setError(errorMessage(cause));
      return null;
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }, [client]);

  return useMemo(() => ({ submit, submitting, error }), [submit, submitting, error]);
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Unexpected error";
}
